import { action, set, setProperties } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency';
import type LocationModel from 'garaje/models/location';
import type LocationRoleModel from 'garaje/models/location-role';
import type ImpressionsService from 'garaje/services/impressions';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { RECEPTIONIST } from 'garaje/utils/roles';

interface PreRegRequiredComponentSignature {
  Args: {
    location: LocationModel;
    saveTask: Task<void, [LocationModel]>;
  };
}

export default class PreRegRequiredComponent extends Component<PreRegRequiredComponentSignature> {
  @service declare impressions: ImpressionsService;
  @service declare store: StoreService;

  @tracked hasReceptionist = false;
  @tracked isOpen = false;

  /**
   * enables / disables button visibility
   */
  get isDisableButtonVisible(): boolean {
    const { location } = this.args;

    return (
      this.isOpen &&
      location.preRegistrationRequiredEnabled &&
      !('preRegistrationRequiredEnabled' in location.changedAttributes())
    );
  }

  @action
  cancel(): void {
    const { location } = this.args;
    if (<boolean>(<unknown>location.hasDirtyAttributes)) {
      location.rollbackAttributes();
    }
    this.isOpen = false;
  }

  @action
  toggleHostApprovalEnabled(): void {
    const { location } = this.args;
    set(location, 'hostApprovalEnabled', !location.hostApprovalEnabled);
  }

  @action
  enablePreregistrationRequired(): void {
    const { location } = this.args;
    set(location, 'preRegistrationRequiredEnabled', true);
    this.isOpen = true;
  }

  @action
  async disablePreregistrationRequired(): Promise<void> {
    const { location } = this.args;
    setProperties(location, { preRegistrationRequiredEnabled: false, hostApprovalEnabled: false });
    await this.saveLocation();
  }

  @action
  async saveLocation(): Promise<void> {
    const { location, saveTask } = this.args;
    await saveTask.perform(location);
    this.isOpen = false;
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_PRE_REGISTRATION_REQUIRED[
        location.preRegistrationRequiredEnabled ? 'ENABLED' : 'DISABLED'
      ],
    );
  }

  /**
   * Task used for loading receptionists from the server if they don't already exist in the store
   */
  loadFrontDeskAdmin = dropTask(async () => {
    const { location } = this.args;
    const cachedReceptionists = this.store.peekAll('location-role').filter((role) => role.roleName === RECEPTIONIST);
    let receptionists: LocationRoleModel[] = [];

    if (!cachedReceptionists.length) {
      receptionists = (
        await this.store.query('location-role', {
          filter: {
            location: location.id,
            roles: RECEPTIONIST,
          },
        })
      ).slice();
    }

    this.hasReceptionist = cachedReceptionists.length > 0 || receptionists.length > 0;
  });
}
